import { BigNumber } from '@ethersproject/bignumber';
import { formatUnits } from '@ethersproject/units';
import { Info } from '@phosphor-icons/react';
import Tippy from '@tippyjs/react';
import { useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { Network } from '@manifoldxyz/js-ts-utils';
import { coinbaseApi } from '@/api/coinbase';
import { ClaimStudioInstance } from '@/api/models/studioInstance';
import { getExtensionAddress } from '@/common/util';
import ClaimExtensionContract from '@/contracts/claimExtensionContract';
import { useReservoirFloorToken } from '@/hooks/useReservoirFloorToken';
import useMainStore from '@/store/mainStore';

/**
 * @dev we do not display the sum total of price and fee
 * because the fee and the price may be in disparate currencies.
 */
export default function CheckoutSecondaryPriceDetails() {
  const studioInstance = useMainStore((state) => state.studioInstance);
  const numberOfMints = useMainStore((state) => state.numberOfMints);

  // Use hook to ensure we are showing the latest floor token data
  const { floorToken: reservoirFloorToken, isLoading: reservoirFloorTokenIsLoading } =
    useReservoirFloorToken();

  const [mintFeeString, setMintFeeString] = useState('');
  const [totalPriceString, setTotalPriceString] = useState<string | null>(null);

  const [mintFeeBN, setMintFeeBN] = useState<BigNumber | null>(null); // goes with mintFeeString

  const [totalPriceUsdValue, setTotalPriceUsdValue] = useState<string | null>(null);
  const [isLoadingUsdValue, setIsLoadingUsdValue] = useState(false);

  const [feeUsdRate, setFeeUsdRate] = useState<number | null>(null);

  // Calculate total
  useEffect(() => {
    const calculateCombinedTotal = async () => {
      if (!studioInstance || !mintFeeBN) return;

      // Get native currency for the network
      const nativeCurrency =
        Network.NETWORK_CONFIGS[
          studioInstance.publicData.network as keyof typeof Network.NETWORK_CONFIGS
        ].nativeCurrency;

      if (
        nativeCurrency.symbol.toLowerCase() ===
        reservoirFloorToken?.market.floorAsk.price.currency.symbol.toLowerCase()
      ) {
        const totalPrice = mintFeeBN.add(
          BigNumber.from(reservoirFloorToken?.market.floorAsk.price.amount.raw),
        );

        const formattedTotalPrice = formatUnits(totalPrice.toString(), nativeCurrency.decimals);

        setTotalPriceString(formattedTotalPrice + ' ' + nativeCurrency.symbol);
      } else {
        setTotalPriceString('different_currencies');
      }
    };

    calculateCombinedTotal();
  }, [studioInstance, mintFeeBN, reservoirFloorToken]);

  // Fetch USD rates for fee
  // Done on its own to reduce fetch calls to only what's needed
  // No need to fetch the price's USD rate because it's already in the floorAsk object
  useEffect(() => {
    const calculateUsdValue = async () => {
      if (!studioInstance) return;

      setIsLoadingUsdValue(true);

      try {
        // Get native currency for the network
        const nativeCurrency =
          Network.NETWORK_CONFIGS[
            studioInstance.publicData.network as keyof typeof Network.NETWORK_CONFIGS
          ].nativeCurrency;

        // Get fee USD rate (always in native currency)
        const fetchedFeeUsdRate = await coinbaseApi.getCurrencySymbolUsdRate(nativeCurrency.symbol);

        setFeeUsdRate(fetchedFeeUsdRate ?? null);
      } catch (error) {
        console.error('Error calculating USD value:', error);
        setTotalPriceUsdValue(null);
      } finally {
        setIsLoadingUsdValue(false);
      }
    };

    calculateUsdValue();
  }, [studioInstance]);

  // Calculate USD value for total price (not subtotal, not fee, not price per mint)
  useEffect(() => {
    const calculateUsdValue = async () => {
      if (!studioInstance || !mintFeeBN) return;

      setIsLoadingUsdValue(true);

      try {
        // Get native currency for the network
        const nativeCurrency =
          Network.NETWORK_CONFIGS[
            studioInstance.publicData.network as keyof typeof Network.NETWORK_CONFIGS
          ].nativeCurrency;

        // If rate is undefined, we can't calculate USD value
        if (!feeUsdRate) {
          setTotalPriceUsdValue(null);
          return;
        }

        const formattedMintFee = formatUnits(mintFeeBN.toString(), nativeCurrency.decimals);

        // Calculate USD values
        const feeUsdValue = parseFloat(formattedMintFee) * feeUsdRate;
        const subtotalUsdValue = reservoirFloorToken?.market.floorAsk.price.amount.usd;

        // Sum USD values
        const totalUsd = feeUsdValue + (subtotalUsdValue ?? 0);

        // if the price is less than 1 cent show the string "<$0.01"
        if (totalUsd < 0.01) {
          setTotalPriceUsdValue('<$0.01 USD');
        } else {
          // Format to 2 decimal places with USD symbol
          setTotalPriceUsdValue(`$${totalUsd.toFixed(2)} USD`);
        }
      } catch (error) {
        console.error('Error calculating USD value:', error);
        setTotalPriceUsdValue(null);
      } finally {
        setIsLoadingUsdValue(false);
      }
    };

    calculateUsdValue();
  }, [studioInstance, mintFeeBN, feeUsdRate, reservoirFloorToken]);

  // Calculate and format mint fee
  useEffect(() => {
    const asyncHandler = async () => {
      if (!studioInstance) return;

      // @note number of mints is always 1 in this screen

      const claimExtensionContract = new ClaimExtensionContract(
        studioInstance.publicData.network,
        getExtensionAddress(studioInstance),
        studioInstance.publicData.contract.contractAddress,
        parseInt(
          (studioInstance as ClaimStudioInstance).publicData?.claimIndex?.toString() ||
            studioInstance.id,
        ),
      );

      const nativeCurrency =
        Network.NETWORK_CONFIGS[
          studioInstance.publicData.network as keyof typeof Network.NETWORK_CONFIGS
        ].nativeCurrency;

      const newMintFee = await claimExtensionContract.getMintFee();
      const totalMintFee = newMintFee.mul(numberOfMints);
      setMintFeeBN(totalMintFee);

      const formattedMintFee = formatUnits(totalMintFee.toString(), nativeCurrency.decimals);
      setMintFeeString(formattedMintFee + ' ' + nativeCurrency.symbol);
    };
    asyncHandler();
  }, [numberOfMints, studioInstance]);

  return (
    <div className="m-claim--flex m-claim--flex-col m-claim--gap-2">
      {/* Price and fee section in a condensed format */}
      <div className="m-claim--grid m-claim--grid-cols-2 m-claim--gap-1 m-claim--text-sm">
        <div className="m-claim--flex m-claim--items-center">
          <span>Price</span>
        </div>
        <div className="m-claim--flex m-claim--justify-end">
          {!reservoirFloorTokenIsLoading &&
          reservoirFloorToken?.market.floorAsk.price.amount.decimal ? (
            <span>
              {reservoirFloorToken.market.floorAsk.price.amount.decimal}{' '}
              {reservoirFloorToken.market.floorAsk.price.currency.symbol}
            </span>
          ) : (
            <Skeleton width={80} />
          )}
        </div>

        <div className="m-claim--flex m-claim--items-center m-claim--text-muted">
          <span className="m-claim--flex m-claim--items-center m-claim--gap-0.5">
            Manifold Fee
            <Tippy
              content="This fee helps Manifold maintain our infrastructure and innovate on new tools for creators"
              maxWidth={200}
            >
              <span className="m-claim--ml-0.5 m-claim--flex m-claim--items-center">
                <Info
                  size={14}
                  weight="bold"
                />
              </span>
            </Tippy>
          </span>
        </div>
        <div className="m-claim--flex m-claim--justify-end m-claim--text-muted">
          {mintFeeString ? <span>{mintFeeString}</span> : <Skeleton width={80} />}
        </div>
      </div>

      {/* Total section with condensed divider */}
      {totalPriceString && (
        <>
          <hr className="m-claim--my-1 m-claim--border-t m-claim--border-default" />
          <div className="m-claim--flex m-claim--flex-col m-claim--gap-0.5">
            <div className="m-claim--flex m-claim--justify-between m-claim--font-semibold">
              <span>Total</span>
              {totalPriceString === 'different_currencies' ? (
                <div className="m-claim--flex m-claim--items-end m-claim--gap-1">
                  <span>
                    {reservoirFloorToken?.market.floorAsk.price.amount.decimal}{' '}
                    {reservoirFloorToken?.market.floorAsk.price.currency.symbol}
                  </span>
                  <span className="m-claim--flex m-claim--items-center">
                    <span className="m-claim--mx-0.5 m-claim--text-xs">+</span>
                    <span>{mintFeeString}</span>
                  </span>
                </div>
              ) : (
                <span>{totalPriceString}</span>
              )}
            </div>

            {/* USD value display -- hide if after fetch we have no value to show */}
            {isLoadingUsdValue ? (
              <div className="m-claim--flex m-claim--justify-end m-claim--text-xs m-claim--text-muted">
                <Skeleton width={60} />
              </div>
            ) : totalPriceUsdValue ? (
              <div className="m-claim--flex m-claim--justify-end m-claim--text-xs m-claim--text-muted">
                <span>≈ {totalPriceUsdValue}</span>
              </div>
            ) : null}
          </div>
        </>
      )}
    </div>
  );
}
